import {DropdownItem, openDeleteDialog, openDropdown} from 'platform/components';
import {Box, Center, Clickable, Icon, Image, Show, Spinner} from 'platform/foundation';
import styled from 'styled-components';

import {CSSProperties} from 'react';

import {isNil, isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps, useBoolean} from 'shared/web';

import {Video} from './types/video';

interface VideoPreviewProps extends TestIdProps {
  videoAsset: Video;
  ratio?: CSSProperties['aspectRatio'];
  isLoading?: boolean;
  isDisabled?: boolean;
  onOpenPreview?: () => void;
  onDeleteVideo?: (videoAsset: Video) => void;
}

export function VideoPreview(props: VideoPreviewProps) {
  const [isHovered, handleHover, handleLeave] = useBoolean();
  const [isFocused, handleFocus, handleBlur] = useBoolean();
  const [isPreviewLoaded, handlePreviewLoad] = useBoolean();
  const [isPreviewError, handlePreviewError] = useBoolean();

  const getDropdownItems = (videoAsset: Video) => (
    <DropdownItem
      label={i18n.t('general.actions.delete')}
      leftIcon="action/delete"
      onClick={() => openDeleteDialog({onConfirm: () => props.onDeleteVideo?.(videoAsset)})}
    />
  );

  return (
    <Box
      position="relative"
      borderRadius="small"
      overflow="hidden"
      backgroundColor="palettes.neutral.30.100"
      ratio={props.ratio}
      width="100%"
      height={isNil(props.ratio) ? '100%' : undefined}
    >
      <Clickable
        width="100%"
        height="100%"
        onClick={props.onOpenPreview}
        onMouseEnter={handleHover}
        onMouseLeave={handleLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onContextMenu={(event) => {
          event.preventDefault();
          if (!props.isDisabled && props.onDeleteVideo) {
            openDropdown(event, getDropdownItems(props.videoAsset));
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            props.onOpenPreview?.();
          }
        }}
        isDisabled={props.isLoading}
        data-testid={suffixTestId('video', props)}
      >
        <Show when={isNotNil(props.videoAsset.coverImageUrl)}>
          <Image
            src={props.videoAsset.coverImageUrl}
            width="100%"
            height="100%"
            fit="cover"
            data-testid={suffixTestId('coverImage', props)}
          />
        </Show>
        <Show
          when={
            (isHovered || isFocused) && !isPreviewError && isNotNil(props.videoAsset.previewUrl)
          }
        >
          {/* We need to add image to DOM, so browser can load it, but not show it until it is loaded */}
          <Hideable $hidden={!isPreviewLoaded}>
            <Box position="absolute" top={0} left={0} width="100%" height="100%">
              <Image
                src={props.videoAsset.previewUrl}
                width="100%"
                height="100%"
                fit="cover"
                onLoad={handlePreviewLoad}
                onError={handlePreviewError}
                data-testid={suffixTestId('coverImage', props)}
              />
            </Box>
          </Hideable>
        </Show>
        <Box position="absolute" top={0} left={0} width="100%" height="100%">
          <Center width="100%" height="100%" justify="center">
            {props.isLoading ? (
              <Spinner />
            ) : (
              <Icon data-testid={props['data-testid']} value="AV/play_arrow" size={8} />
            )}
          </Center>
        </Box>
        <Box
          opacity={isHovered || isFocused ? 1 : 0}
          transition="opacity 0.2s"
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          backgroundColor="palettes.black.900.20"
          borderRadius="small"
          tabIndex={0}
        />
      </Clickable>
    </Box>
  );
}

const Hideable = styled(Box)<{$hidden?: boolean}>`
  ${({$hidden}) => $hidden && 'display: none;'}
`;
